import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { loadTenant } from "../../lib/tenant";
import { useLocation, useNavigate } from "react-router-dom";

const LMS_GREEN = "#8BC53F";

// Poll settings
const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 120000;

const PAGE_SIZE = 10;

export default function Courses() {
  const location = useLocation();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState(undefined); // undefined=loading, null=not found, object=tenant

  const [courses, setCourses] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState(null);

  const [enrolments, setEnrolments] = useState([]);
  const [loadingCourseId, setLoadingCourseId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  // Track active poll timers per enrolment/course to avoid duplicates
  const pollersRef = useRef(new Map());

  // Read tab from query string: all | assigned | completed
  const tab = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const t = (q.get("tab") || "").toLowerCase();
    if (t === "assigned" || t === "completed") return t;
    return "all";
  }, [location.search]);

  // Reset to page 1 when tab changes
  useEffect(() => {
    setCurrentPage(1);
    setSearchQuery("");
  }, [tab]);

  const safeReadJson = async (resp) => {
    try {
      return await resp.json();
    } catch {
      return null;
    }
  };

  const refreshEnrolments = useCallback(async () => {
    if (!userId) return;
    if (!tenant?.id) return;

    const { data, error } = await supabase
      .from("enrolments")
      .select("id, course_id, scormcloud_registration_id, completed, completed_at")
      .eq("tenant_id", tenant.id)
      .eq("user_id", userId);

    if (!error) setEnrolments(data || []);
  }, [userId, tenant?.id]);

  const startCompletionPolling = useCallback(
    async ({ enrolmentId, courseId }) => {
      // Prevent duplicate pollers for same enrolment
      const existing = pollersRef.current.get(enrolmentId);
      if (existing) return;

      const { data: sessionRes } = await supabase.auth.getSession();
      const accessToken = sessionRes?.session?.access_token;

      if (!accessToken) return;

      const startedAt = Date.now();

      const interval = setInterval(async () => {
        try {
          // Timeout
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            clearInterval(interval);
            pollersRef.current.delete(enrolmentId);
            return;
          }

          const resp = await fetch("/api/scorm/sync", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ enrolmentId, accessToken }),
          });

          const payload = await safeReadJson(resp);

          // If server says completed, refresh and stop polling
          if (payload?.completed) {
            await refreshEnrolments();
            clearInterval(interval);
            pollersRef.current.delete(enrolmentId);
          }
        } catch {
          // Ignore transient errors; keep polling until timeout
        }
      }, POLL_INTERVAL_MS);

      pollersRef.current.set(enrolmentId, { interval, courseId });
    },
    [refreshEnrolments]
  );

  // 0) Load tenant (based on subdomain)
  useEffect(() => {
    let cancelled = false;

    const initTenant = async () => {
      try {
        const t = await loadTenant();
        if (!cancelled) setTenant(t || null);
      } catch (e) {
        console.error("Failed to load tenant:", e);
        if (!cancelled) setTenant(null);
      }
    };

    initTenant();
    return () => {
      cancelled = true;
    };
  }, []);

  // 1) Load logged-in user
  useEffect(() => {
    let cancelled = false;

    const loadUser = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (cancelled) return;
      if (error || !data?.user) return;

      setUserId(data.user.id);
      setUserEmail(data.user.email || null);
    };

    loadUser();
    return () => {
      cancelled = true;
    };
  }, []);

  // 2) Load course catalogue (tenant-scoped)
  useEffect(() => {
    let cancelled = false;

    const loadCourses = async () => {
      if (tenant === undefined) return;

      if (!tenant?.id) {
        setCourses([]);
        setLoadingList(false);
        return;
      }

      setLoadingList(true);

      const { data, error } = await supabase
        .from("courses")
        .select("id, title, description, image_url, scormcloud_course_id, active, created_at")
        .eq("tenant_id", tenant.id)
        .eq("active", true)
        .order("created_at", { ascending: false });

      if (cancelled) return;

      setCourses(error ? [] : data || []);
      setLoadingList(false);
    };

    loadCourses();
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  // 3) Load enrolments (tenant-scoped)
  useEffect(() => {
    let cancelled = false;
    if (!userId) return;
    if (!tenant?.id) return;

    const loadEnrolments = async () => {
      const { data } = await supabase
        .from("enrolments")
        .select("id, course_id, scormcloud_registration_id, completed, completed_at")
        .eq("tenant_id", tenant.id)
        .eq("user_id", userId);

      if (!cancelled) setEnrolments(data || []);
    };

    loadEnrolments();
    return () => {
      cancelled = true;
    };
  }, [userId, tenant?.id]);

  // 4) On page load, start polling incomplete enrolments
  useEffect(() => {
    if (!enrolments?.length) return;

    for (const e of enrolments) {
      if (!e.completed && e.id) {
        if (e.scormcloud_registration_id) {
          startCompletionPolling({ enrolmentId: e.id, courseId: e.course_id });
        }
      }
    }
  }, [enrolments, startCompletionPolling]);

  // Cleanup pollers on unmount
  useEffect(() => {
    return () => {
      for (const { interval } of pollersRef.current.values()) {
        clearInterval(interval);
      }
      pollersRef.current.clear();
    };
  }, []);

  const enrolmentByCourseId = useMemo(() => {
    const m = new Map();
    for (const e of enrolments) m.set(e.course_id, e);
    return m;
  }, [enrolments]);

  // Filter courses
  const visibleCourses = useMemo(() => {
    let filtered;
    if (tab === "all") {
      filtered = courses;
    } else if (tab === "assigned") {
      filtered = courses.filter((c) => {
        const e = enrolmentByCourseId.get(c.id);
        return e && !e.completed;
      });
    } else {
      filtered = courses.filter((c) => enrolmentByCourseId.get(c.id)?.completed);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.title?.toLowerCase().includes(q) ||
          c.description?.toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [tab, courses, enrolmentByCourseId, searchQuery]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(visibleCourses.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);

  const pagedCourses = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return visibleCourses.slice(start, start + PAGE_SIZE);
  }, [visibleCourses, safePage]);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setTab = (next) => {
    if (next === "all") navigate("/learner/courses");
    else navigate(`/learner/courses?tab=${next}`);
  };

  const launch = async (courseRow) => {
    const courseRowId = courseRow.id;
    setLoadingCourseId(courseRowId);

    try {
      if (!tenant?.id) return;
      if (!userId || !userEmail) return;

      let enrolment = enrolmentByCourseId.get(courseRowId);

      // Create enrolment if missing (tenant-scoped)
      if (!enrolment) {
        const { data, error } = await supabase
          .from("enrolments")
          .insert({
            tenant_id: tenant.id,
            user_id: userId,
            course_id: courseRowId,
            completed: false,
          })
          .select()
          .single();

        if (error || !data) {
          console.error("Failed to create enrolment:", error);
          alert("Could not enrol you in the course. Please try again.");
          return;
        }

        enrolment = data;
        setEnrolments((prev) => [...prev, data]);
      }

      const { data: sessionRes } = await supabase.auth.getSession();
      const accessToken = sessionRes?.session?.access_token;
      const email = sessionRes?.session?.user?.email;

      const resp = await fetch("/api/scorm/launch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ courseRowId, accessToken, email }),
      });

      const payload = await safeReadJson(resp);

      if (!resp.ok) {
        console.error("SCORM launch failed:", resp.status, payload);
        alert(
          `Course launch failed (HTTP ${resp.status}).\n` +
            `${payload?.error || payload?.message || "No error message returned."}`
        );
        return;
      }

      if (!payload?.launchLink) {
        console.error("SCORM launch missing launchLink:", resp.status, payload);
        alert("Course launch failed: no launch link returned.");
        return;
      }

      // Save registrationId against the EXACT enrolment row
      if (payload.registrationId && enrolment?.id) {
        await supabase
          .from("enrolments")
          .update({ scormcloud_registration_id: payload.registrationId })
          .eq("id", enrolment.id);

        setEnrolments((prev) =>
          prev.map((e) =>
            e.id === enrolment.id
              ? { ...e, scormcloud_registration_id: payload.registrationId }
              : e
          )
        );
      }

      // Open SCORM in a new tab
      window.open(payload.launchLink, "_blank", "noopener,noreferrer");

      // Start polling to flip enrolment.completed when SCORM Cloud says complete
      if (enrolment?.id) {
        await startCompletionPolling({ enrolmentId: enrolment.id, courseId: courseRowId });
      }
    } finally {
      setLoadingCourseId(null);
    }
  };

  // If root domain returns no tenant, show a friendly message
  if (tenant === null) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl shadow p-6 text-sm text-gray-700">
          Please use your company link (e.g. coop.vibelms.com.au).
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="bg-white rounded-xl shadow p-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {tab === "assigned"
            ? "Assigned Courses"
            : tab === "completed"
            ? "Completed Courses"
            : "Course Catalogue"}
        </h1>

        <p className="mt-1 text-sm text-gray-500">
          {tab === "assigned"
            ? "Courses that have been assigned to you — work through these at your own pace."
            : tab === "completed"
            ? "Courses you have already finished."
            : "Browse all available courses in the catalogue. Completing them is not mandatory unless they appear in your Assigned tab."}
        </p>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Search input */}
          <div className="relative flex-1 max-w-sm">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search courses…"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="w-full pl-9 pr-4 py-1.5 rounded border text-sm text-gray-800 outline-none focus:ring-2"
              style={{ borderColor: "#e5e7eb", focusRingColor: LMS_GREEN }}
              onFocus={(e) => { e.currentTarget.style.borderColor = LMS_GREEN; e.currentTarget.style.boxShadow = `0 0 0 2px ${LMS_GREEN}33`; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.boxShadow = "none"; }}
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(""); setCurrentPage(1); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Tab buttons */}
          <div className="flex gap-2">
          {[
            { key: "all", label: "All Courses" },
            { key: "assigned", label: "Assigned to Me" },
            { key: "completed", label: "Completed" },
          ].map(({ key, label }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-4 py-1.5 rounded border text-sm transition ${
                  active ? "text-white" : "bg-white text-gray-800"
                }`}
                style={
                  active
                    ? { backgroundColor: LMS_GREEN, borderColor: LMS_GREEN }
                    : { borderColor: "#e5e7eb" }
                }
              >
                {label}
              </button>
            );
          })}
          </div>
        </div>
      </div>

      {loadingList ? (
        <div className="bg-white rounded-xl shadow p-6 text-sm">Loading courses…</div>
      ) : visibleCourses.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-6 text-sm text-gray-600">
          {searchQuery.trim() ? `No courses found matching "${searchQuery}".` : "No courses to show."}
        </div>
      ) : (
        <>
          <div className="space-y-5">
            {pagedCourses.map((c) => {
              const enrol = enrolmentByCourseId.get(c.id);
              const isCompleted = enrol?.completed === true;
              const isLoading = loadingCourseId === c.id;

              const buttonLabel = isLoading
                ? "Working…"
                : isCompleted
                ? "View"
                : enrol
                ? "Continue"
                : "Enrol & Start";

              return (
                <div
                  key={c.id}
                  className="bg-white rounded-xl shadow border overflow-hidden flex flex-col md:flex-row"
                >
                  <div className="md:w-80 w-full bg-gray-100">
                    <img
                      src={c.image_url || "/receipting.jpg"}
                      alt={c.title}
                      className="w-full h-48 md:h-full md:min-h-[190px] object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.src = "/receipting.jpg";
                      }}
                    />
                  </div>

                  <div className="flex-1 p-6 flex flex-col">
                    <div className="text-xl font-bold leading-snug" style={{ color: LMS_GREEN }}>
                      {c.title}
                    </div>

                    {c.description ? (
                      <div className="text-sm text-gray-700 mt-2 line-clamp-3">
                        {c.description}
                      </div>
                    ) : null}

                    <div className="text-xs text-gray-500 mt-3">
                      {isCompleted ? "Completed" : enrol ? "Enrolled" : "Not started"}
                    </div>

                    <div className="mt-4 md:mt-auto md:flex md:justify-end">
                      <button
                        onClick={() => launch(c)}
                        disabled={isLoading}
                        className="px-6 py-2 rounded-lg text-white font-semibold disabled:opacity-50 transition"
                        style={{ backgroundColor: LMS_GREEN }}
                        onMouseEnter={(e) => {
                          if (!isLoading) e.currentTarget.style.filter = "brightness(0.95)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.filter = "none";
                        }}
                      >
                        {buttonLabel}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="bg-white rounded-xl shadow p-4 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {(safePage - 1) * PAGE_SIZE + 1}–
                {Math.min(safePage * PAGE_SIZE, visibleCourses.length)} of{" "}
                {visibleCourses.length} courses
              </p>

              <div className="flex gap-1">
                <button
                  onClick={() => goToPage(safePage - 1)}
                  disabled={safePage === 1}
                  className="px-3 py-1.5 rounded border text-sm disabled:opacity-40 bg-white text-gray-700 hover:bg-gray-50 transition"
                  style={{ borderColor: "#e5e7eb" }}
                >
                  ‹
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  const active = page === safePage;
                  return (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className="px-3 py-1.5 rounded border text-sm transition"
                      style={
                        active
                          ? { backgroundColor: LMS_GREEN, borderColor: LMS_GREEN, color: "white" }
                          : { borderColor: "#e5e7eb", backgroundColor: "white", color: "#374151" }
                      }
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  onClick={() => goToPage(safePage + 1)}
                  disabled={safePage === totalPages}
                  className="px-3 py-1.5 rounded border text-sm disabled:opacity-40 bg-white text-gray-700 hover:bg-gray-50 transition"
                  style={{ borderColor: "#e5e7eb" }}
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
